import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayLogo } from '@/components/ui/pay-logo';
import { getWebApp, haptic, openInvoiceUrl, setMainButtonText } from '@/lib/telegram';
import { rub } from '@/lib/currency';
import { api } from '@/lib/api';
import { SectionLabel, OptionRow, OptionGroup } from '@/components/ui/calm';
import { SkeletonCard } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { providerLabel } from '@/lib/providers';
import { SbpRow } from '@/components/ui/provider-pills';
import { SBP_GROUP, useSbpGroup } from '@/lib/sbp-group';
import { toast } from 'sonner';
import { useMutation, useQuery } from '@tanstack/react-query';

export const Route = createFileRoute('/topup')({
  component: Topup,
});


// Пресеты в display-минор. Юзер также может ввести своё.
const PRESETS_MINOR = [50000, 100000, 200000, 500000]; // 500 / 1000 / 2000 / 5000 ₽

function Topup() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { data: providersData, isPending: providersPending } = useQuery({
    queryKey: ['payment-providers'],
    queryFn: () => api.paymentProviders(),
  });
  // Первый способ из списка сервера (СБП), а не CryptoBot: см. checkout.$planId.tsx.
  const [provider, setProvider] = useState<string | null>(null);
  const [amountMinor, setAmountMinor] = useState<number>(PRESETS_MINOR[0]);
  const [customInput, setCustomInput] = useState<string>('');

  useEffect(() => {
    const providers = providersData?.providers ?? [];
    if (providers.length > 0 && (provider === null || !providers.includes(provider))) {
      setProvider(providers[0]);
    }
  }, [providersData, provider]);

  // Замок на повторный тап по MainButton — см. checkout.$planId.tsx.
  const topupBusyRef = useRef(false);

  const { mutate: doTopup, isPending } = useMutation({
    mutationFn: () => api.topupBalance(amountMinor, provider ?? ''),
    onSettled: () => {
      topupBusyRef.current = false;
    },
    onSuccess: (intent) => {
      openInvoiceUrl(intent.invoice_url);
      navigate({ to: '/processing/$paymentId', params: { paymentId: intent.payment_id } });
    },
    onError: (err: Error) => {
      haptic('error');
      toast.error(err.message || t('Invoice failed'));
    },
  });

  const validAmount = amountMinor >= 100;
  // Сбор за оплату виден заранее: человек кладёт 500, а платит 525, и узнавать об
  // этом на странице провайдера — худший момент.
  const feePercent = providersData?.fee_percent ?? 0;
  const topupTotal = Math.round((amountMinor * (1 + feePercent / 100)) / 100);
  const providers = providersData?.providers ?? [];
  const sbp = useSbpGroup(providers, provider, setProvider);

  useEffect(() => {
    const tg = getWebApp();
    if (!tg?.BackButton || !tg?.MainButton) return;
    tg.BackButton.show();
    const onBack = () => navigate({ to: '/wallet' });
    tg.BackButton.onClick(onBack);

    setMainButtonText(t('Topup', { amount: amountMinor / 100 }));
    tg.MainButton.show();
    const onMain = () => {
      if (!validAmount || provider === null || topupBusyRef.current) return;
      topupBusyRef.current = true;
      doTopup();
    };
    tg.MainButton.onClick(onMain);
    if (isPending) {
      tg.MainButton.showProgress();
    } else {
      // hideProgress() в SDK Telegram сам включает кнопку, поэтому он идёт ДО
      // disable(): в обратном порядке кнопка выглядела активной и не нажималась.
      tg.MainButton.hideProgress();
      if (!validAmount || provider === null) tg.MainButton.disable();
      else tg.MainButton.enable();
    }

    return () => {
      tg.BackButton.offClick(onBack);
      tg.MainButton.offClick(onMain);
      tg.MainButton.hideProgress();
    };
  }, [navigate, amountMinor, provider, t, doTopup, isPending, validAmount]);

  return (
    <div className="p-4 space-y-6 pb-24">
      <div>
        <h2 className="tl-h text-[32px] font-semibold leading-tight tracking-[-0.035em] mb-2 text-foreground">{t('Topup title')}</h2>
        <p className="text-sm text-[var(--t-muted)]">{t('Topup desc')}</p>
      </div>

      <div>
        <SectionLabel className="mb-3">{t('Amount')}</SectionLabel>
        <div className="grid grid-cols-2 gap-2">
          {PRESETS_MINOR.map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => {
                setAmountMinor(m);
                setCustomInput('');
              }}
              className={cn(
                'p-3 rounded-[16px] text-sm font-bold transition-transform duration-100 active:scale-[0.98] tabular',
                amountMinor === m && customInput === '' ? 'border-transparent bg-foreground text-background' : 'calm-card text-foreground',
              )}
            >
              {rub(m / 100)}
            </button>
          ))}
        </div>
        <div className="mt-3">
          <label className="text-xs text-[var(--t-muted)]">{t('Custom amount')}</label>
          <input
            type="number"
            min={1}
            max={100000}
            value={customInput}
            onChange={(e) => {
              setCustomInput(e.target.value);
              const v = Number(e.target.value);
              if (Number.isFinite(v) && v > 0) setAmountMinor(Math.floor(v * 100));
            }}
            placeholder={t('Custom amount hint')}
            className="mt-1 w-full calm-card rounded-[var(--r-card)] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-accent text-foreground"
          />
        </div>
      </div>

      {feePercent > 0 && validAmount && (
        <p className="text-xs text-[var(--t-muted)] -mt-2">
          {t('Topup fee note', {
            total: topupTotal,
            percent: feePercent,
            amount: Math.round(amountMinor / 100),
          })}
        </p>
      )}

      <div>
        <SectionLabel className="mb-3">{t('Payment Method')}</SectionLabel>
        {providersPending ? (
          // Дефолт `['cryptobot']` до ответа показывал бы способ оплаты, которого
          // у партнёра может и не быть, и он подменялся бы под пальцем.
          <div className="space-y-2">
            <SkeletonCard className="h-[58px] min-h-0 rounded-xl" />
            <SkeletonCard className="h-[58px] min-h-0 rounded-xl" />
          </div>
        ) : (
        <OptionGroup>
          {sbp.rows.map((p) =>
            p === SBP_GROUP ? (
              <SbpRow key={p} group={sbp} />
            ) : (
              <OptionRow
                key={p}
                leading={<PayLogo provider={p} />}
                title={providerLabel(p)}
                selected={provider === p}
                onSelect={() => setProvider(p)}
                className={sbp.isVariant(p) ? 'pl-8' : undefined}
              />
            ),
          )}
          {providers.length === 0 && (
            <div className="p-4 text-sm text-[var(--t-muted)]">
              {t('No providers')}
            </div>
          )}
        </OptionGroup>
        )}
      </div>
    </div>
  );
}
